// Special Follow-ups Review (approved HTML:
// 17-Special-Follow-ups-Consistency-Review-v5.html).
//
// Shared conversation view with task variants. Send, Finish and
// retained-attention remain distinct. Unknown requests reconcile once
// and never advance work. Writes gate on LIVE_WRITE_GATE_FOLLOWUP_SEND.
//
// W31 (2026-10-20): on a real session the screen maps the REAL body of
// GET /v1/contacts/:id/follow-ups (`followUps[]`, HubSpot TODO tasks,
// MEASURED `[]` for Jodi in the W30 audit) instead of dereferencing
// `payload.primaryTask.draft` (the crash "reading 'draft'"). With no
// task the screen says "No follow-up assigned" and, when the profile
// carries a VanillaSoft callback date, shows that date; nothing else.
//
// W35 (2026-10-20): the profile now carries `callBackDateVs` (W34; Jodi
// 573130771181 = "2026-11-18", Karla and Tere null, MEASURED by W34
// against the local api-server). With no HubSpot task but a callback
// date the screen renders "Callback requested for 2026-11-18" as the
// follow-up row (hero, conversation card, task history and the current
// task panel). With neither it keeps "No follow-up assigned". The date is
// shown as HubSpot stores it (YYYY-MM-DD); nothing is reformatted or
// inferred from it.
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { Icon } from '../_shared/IconSprite.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { RepriorityPill } from '../_shared/PriorityFocus.js';
import { QueueUpNext } from '../_shared/UpNext.js';
import {
  PRIMARY_TASK as PRIMARY_TASK_FIXTURE,
  HORSE as HORSE_FIXTURE,
  NEXT_UP as NEXT_UP_FIXTURE,
} from './mockData.js';
import { SARAH, HORSE_PORTRAIT_PLACEHOLDER } from '../../data/mockData.js';
import { getReviewReturnTarget } from '../_shared/ReviewState.js';
import { useScopedData } from '../_w2a_lower/scopedData.js';
import {
  isRecord,
  monthYear,
  readOptionalString,
  readString,
  useScreenIdentity,
} from '../_w2a_lower/identity.js';
import { WriteButton } from '../_w2a_lower/liveWrite.js';
import './styles.css';

interface SpecialFollowupsFixture {
  primaryTask: typeof PRIMARY_TASK_FIXTURE;
  horse: typeof HORSE_FIXTURE;
  nextUp: typeof NEXT_UP_FIXTURE;
}

const SPECIAL_FOLLOWUPS_FIXTURE: SpecialFollowupsFixture = {
  primaryTask: PRIMARY_TASK_FIXTURE,
  horse: HORSE_FIXTURE,
  nextUp: NEXT_UP_FIXTURE,
};

/** One HubSpot TODO task row as the api-server returns it (READ: sinks/follow-ups-sink.mjs toRow). */
export interface RealFollowUp {
  id: string;
  subject: string;
  body: string;
  status: string | null;
  priority: string | null;
  dueAt: string | null;
}

/**
 * Map the REAL api-server body to task rows.
 *
 * MEASURED 2026-10-20, GET /v1/contacts/573130771181/follow-ups:
 *   { result:'OK', contactId:'573130771181', followUps: [] }
 * Row shape when non-empty (READ from follow-ups-sink.mjs):
 *   { id, subject, body, status, priority, type, dueAt, completedAt, ownerId, createdAt, updatedAt }
 */
export function mapRealFollowUps(body: unknown): RealFollowUp[] {
  const list = isRecord(body) && Array.isArray(body.followUps) ? body.followUps : [];
  const out: RealFollowUp[] = [];
  list.forEach((raw, index) => {
    if (!isRecord(raw)) return;
    out.push({
      id: readString(raw.id) || `task-${index + 1}`,
      subject: readString(raw.subject),
      body: readString(raw.body),
      status: readOptionalString(raw.status),
      priority: readOptionalString(raw.priority),
      dueAt: readOptionalString(raw.dueAt),
    });
  });
  return out;
}

function titleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** W35: the follow-up row for a HubSpot `call_back_date_vs` value, e.g. "Callback requested for 2026-11-18". */
export function callbackLabelFor(callBackDate: string): string {
  return `Callback requested for ${callBackDate}`;
}

const CALLBACK_SOURCE_NOTE =
  'Recorded on the HubSpot contact by the VanillaSoft import (call_back_date_vs). No HubSpot task is attached yet.';

export interface SpecialFollowupsScreenProps {
  stateOverride?: BoundaryState;
}

export function SpecialFollowupsScreen({
  stateOverride,
}: SpecialFollowupsScreenProps = {}): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const customerId = params.customerId ?? SARAH.id;
  const identity = useScreenIdentity(customerId);
  const scoped = useScopedData<unknown>({
    path: `/v1/contacts/${encodeURIComponent(customerId)}/follow-ups`,
    fixture: SPECIAL_FOLLOWUPS_FIXTURE,
    loadingLabel: 'Loading follow-ups…',
    errorTitle: 'Follow-ups did not load',
    errorCode: 'SPECIAL_FOLLOWUPS_UNAVAILABLE',
    // `followUps: []` is a valid answer; the honest empty renders inline.
    isEmpty: () => false,
    ...(stateOverride !== undefined ? { stateOverride } : {}),
  });
  const useFixture = scoped.previewAllowed;
  const tasks = useMemo<RealFollowUp[]>(
    () => (useFixture ? [] : mapRealFollowUps(scoped.data)),
    [useFixture, scoped.data],
  );
  const task: RealFollowUp | null = tasks[0] ?? null;
  const customer = identity.customer;
  const firstName = identity.firstName;
  const possessive = identity.possessive;
  const paused = identity.liveWritesPaused;
  const profile = identity.profile;
  // W35: VanillaSoft callback date from the profile (null in preview and
  // when HubSpot has no call_back_date_vs). Rendered verbatim.
  const callbackDate = useFixture ? null : identity.callBackDate;
  const callbackLabel = callbackDate ? callbackLabelFor(callbackDate) : null;

  // Fixture fields are read ONLY on the preview path.
  const PRIMARY_TASK = PRIMARY_TASK_FIXTURE;
  const HORSE = HORSE_FIXTURE;
  const NEXT_UP = NEXT_UP_FIXTURE;

  const seedDraft = useFixture ? PRIMARY_TASK.draft : '';
  const [draft, setDraft] = useState(seedDraft);
  useEffect(() => {
    setDraft(seedDraft);
  }, [seedDraft, task?.id]);
  const [sent, setSent] = useState(false);
  const [retained, setRetained] = useState(false);
  const [threadExpanded, setThreadExpanded] = useState(false);
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const state =
    stateOverride ??
    (scoped.state.kind === 'default' ? resolveState(kind, undefined) : scoped.state);
  const finishDisabled = !(sent || retained);
  const returnTarget = getReviewReturnTarget(location.state, customerId);

  const helper = useMemo(() => {
    if (sent) return 'Reply sent · Ready to advance';
    if (retained) return 'Task retained for later';
    return 'Send a reply or choose "Still needs attention"';
  }, [sent, retained]);

  // Hero + dock copy. Preview = design fixture; real = task row or honest empty.
  const hero = useFixture
    ? {
        label: PRIMARY_TASK.label,
        context: PRIMARY_TASK.context,
        purpose: PRIMARY_TASK.purpose,
        reason: PRIMARY_TASK.reason,
      }
    : task
      ? {
          label: 'HubSpot follow-up task',
          context: task.dueAt ? `Due ${monthYear(task.dueAt) ?? task.dueAt}` : 'No due date',
          purpose: task.subject || 'Follow-up task',
          reason: task.body || 'No task notes on the HubSpot task.',
        }
      : callbackLabel
        ? {
            label: 'VanillaSoft callback',
            context: callbackLabel,
            purpose: callbackLabel,
            reason: CALLBACK_SOURCE_NOTE,
          }
        : {
            label: 'No follow-up assigned',
            context: '',
            purpose: 'No follow-up assigned',
            reason: 'When a reactivation or routine task is routed to you it appears here.',
          };
  const destination = useFixture
    ? PRIMARY_TASK.destination
    : customer.phone || customer.email || 'No phone or email on file';
  const channelLabel = useFixture ? 'Text message' : task ? 'HubSpot task' : callbackLabel ? 'VanillaSoft callback' : 'No channel';
  const draftHint = useFixture
    ? `Text to ${PRIMARY_TASK.destination} · ${PRIMARY_TASK.preference}`
    : `Reply to ${destination} · Send channel not connected yet`;

  return (
    <WorkspaceChrome screen="follow-ups" rail="my-work">
      {/* W40: the approved 17 has no breadcrumb row (MEASURED, 0 entries);
          the row stays only as the carrier of the preview label. */}
      {scoped.previewAllowed ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>{customer.fullName}</strong>{' '}
            <span className="slash">/</span> Follow-up
          </div>
          <span className="preview">Design preview · Sample follow-up · No live send</span>
        </div>
      ) : null}
      <section className="cc-hero" aria-label="Current task">
        <div className="sf-hero-channel">
          <Icon id="message" />
          <div>
            <span className="eyebrow" id="sf-task-kind">
              SPECIAL FOLLOW-UP
            </span>
            <h1 id="sf-hero-task-label">{hero.label}</h1>
            <span id="sf-hero-task-context">{hero.context}</span>
          </div>
        </div>
        <div className="sf-hero-task-copy">
          <h2 id="sf-purpose">{hero.purpose}</h2>
          <p id="sf-reason">{hero.reason}</p>
        </div>
        {/* W40: approved pill, informational, on every session. */}
        <div className="shared-repriority-heading">
          <RepriorityPill className="shared-repriority" />
        </div>
      </section>
      <IdentityRow
        preferenceNote="The customer’s request guides this conversation."
        customer={customer}
        badgeLabel="Special follow-up"
        {...(identity.sinceLabel !== undefined ? { sinceLabel: identity.sinceLabel } : {})}
      />
      <CustomerTabs customerId={customerId} active="current" />
      <DataBoundary state={state} testId="special-followups-boundary">
        <section className="sf-task-dock" aria-label="Current conversation">
          <div className="sf-task-identity">
            <div className="sf-task-person">
              <span className="sf-task-avatar" aria-hidden="true">
                {customer.initials}
              </span>
              <div>
                <h3 id="sf-dock-recipient">{useFixture ? PRIMARY_TASK.recipient : customer.fullName}</h3>
                <p id="sf-dock-destination">{destination}</p>
              </div>
            </div>
            <span className="sf-task-badge" id="sf-direction-label">
              {hero.label}
            </span>
            <div className="sf-task-dock-cue">
              <b id="sf-dock-cue-title">{useFixture ? 'Text reply & notes' : 'Reply & notes'}</b>
              <span>Notes stay with this conversation</span>
            </div>
          </div>
        </section>
        <div id="sf-task-workspace">
          <div className="sf-grid">
            <section className="card conversation" aria-label="Current follow-up">
              <div className="card-head">
                <div className="row">
                  <span className="head-icon">
                    <Icon id="message" />
                  </span>
                  <h3 id="sf-message-heading">Message to {firstName}</h3>
                </div>
                <span className="sf-channel" id="sf-channel-label">
                  {channelLabel}
                </span>
              </div>
              <div className="thread">
                {useFixture || task ? (
                  <>
                    <div className="sf-thread-toolbar">
                      <span className="eyebrow" id="sf-message-eyebrow">
                        {useFixture ? 'LATEST TEXT MESSAGE' : 'TASK NOTES'}
                      </span>
                      <button
                        type="button"
                        className="sf-thread-toggle"
                        id="sf-thread-expand"
                        aria-expanded={threadExpanded}
                        aria-controls="sf-thread-window"
                        onClick={() => setThreadExpanded(!threadExpanded)}
                      >
                        <span id="sf-thread-expand-label">
                          {threadExpanded ? 'Collapse thread history' : 'Expand thread history'}
                        </span>
                        <Icon id="arrow" />
                      </button>
                    </div>
                    <div
                      className="sf-thread-window"
                      id="sf-thread-window"
                      role="region"
                      tabIndex={0}
                      aria-label="Earlier messages in this thread"
                      hidden={!threadExpanded}
                    >
                      <p className="muted" style={{ padding: '12px 18px' }}>
                        Earlier messages in this thread appear here when the shared conversation
                        adapter is approved.
                      </p>
                    </div>
                    <div className="message-meta sf-last-message-meta">
                      <strong id="sf-message-sender">
                        {useFixture ? PRIMARY_TASK.sender : task?.priority ? `Priority ${titleCase(task.priority)}` : 'HubSpot task'}
                      </strong>
                      <span id="sf-message-time">
                        {useFixture ? PRIMARY_TASK.lastMessageAt : task?.status ? titleCase(task.status) : ''}
                      </span>
                    </div>
                    <p className="bubble sf-latest-message" id="sf-last-message">
                      {useFixture ? PRIMARY_TASK.lastMessage : task?.body || task?.subject || ''}
                    </p>
                  </>
                ) : callbackLabel ? (
                  <div
                    role="status"
                    data-testid="special-followups-callback"
                    className="muted"
                    style={{ padding: '12px 18px' }}
                  >
                    <strong style={{ display: 'block', marginBottom: 4 }}>{callbackLabel}</strong>
                    <span>{CALLBACK_SOURCE_NOTE}</span>
                  </div>
                ) : (
                  <div
                    role="status"
                    data-testid="special-followups-empty"
                    className="muted"
                    style={{ padding: '12px 18px' }}
                  >
                    <strong style={{ display: 'block', marginBottom: 4 }}>No follow-up assigned</strong>
                    <span>When a reactivation or routine task is routed to you it appears here.</span>
                  </div>
                )}
              </div>
              <div className="draft">
                <div className="draft-top">
                  <strong>Prepared reply</strong>
                  <span>Edit before sending</span>
                </div>
                <textarea
                  id="sf-draft"
                  aria-label="Prepared reply"
                  value={draft}
                  onChange={event => setDraft(event.target.value)}
                />
                <div className="draft-bottom">
                  <WriteButton
                    paused={paused}
                    id="sf-send"
                    className="primary"
                    onClick={() => setSent(true)}
                    disabled={!draft.trim() || sent}
                    actionLabel="Send Reply"
                  >
                    <Icon id="icon-send" />
                    {sent ? 'Reply sent' : 'Send Reply'}
                  </WriteButton>
                  <span id="sf-draft-hint">{draftHint}</span>
                </div>
              </div>
              <div className="sf-retain-row">
                <span id="sf-retain-hint">Need information or a later conversation?</span>
                <button
                  type="button"
                  id="sf-retain"
                  onClick={() => setRetained(true)}
                  disabled={retained}
                >
                  {retained ? 'Retained' : 'Still needs attention'}
                </button>
              </div>
              <details className="sf-history" id="sf-history">
                <summary>
                  Task history <span id="sf-history-count">{useFixture ? 0 : tasks.length + (callbackLabel ? 1 : 0)}</span>
                </summary>
                <ol id="sf-history-list">
                  {useFixture
                    ? null
                    : tasks.map(row => (
                        <li key={row.id} data-testid="special-followups-task">
                          {row.subject || 'Follow-up task'}
                          {row.dueAt ? ` · due ${monthYear(row.dueAt) ?? row.dueAt}` : ''}
                          {row.status ? ` · ${titleCase(row.status)}` : ''}
                        </li>
                      ))}
                  {!useFixture && callbackLabel ? (
                    <li data-testid="special-followups-callback-row">{callbackLabel} · VanillaSoft</li>
                  ) : null}
                </ol>
              </details>
            </section>
            <aside className="right-stack">
              <section className="card" id="sf-customer-context">
                <div className="context">
                  <div className="eyebrow">CUSTOMER CONTEXT</div>
                  {useFixture ? (
                    <>
                      <div className="row">
                        <img
                          id="sf-horse-image"
                          className="sf-horse"
                          alt={`Illustrative approved horse portrait for ${HORSE.name}`}
                          src={HORSE_PORTRAIT_PLACEHOLDER}
                        />
                        <div>
                          <h3>{HORSE.name}</h3>
                          <p className="muted">{HORSE.breed}</p>
                        </div>
                      </div>
                      <dl>
                        <dt>Product</dt>
                        <dd>{HORSE.product}</dd>
                        <dt id="sf-detail-label">Subscription</dt>
                        <dd id="sf-detail-value">{HORSE.subscriptionStatus}</dd>
                        <dt>Customer need</dt>
                        <dd id="sf-detail-need">{HORSE.customerNeed}</dd>
                      </dl>
                    </>
                  ) : (
                    <dl data-testid="special-followups-context">
                      <dt>Lifecycle stage</dt>
                      <dd>{profile?.lifecycleStage ? titleCase(profile.lifecycleStage) : 'Not set'}</dd>
                      <dt>Last VanillaSoft call</dt>
                      <dd>{profile?.lastCallDate ?? 'None logged'}</dd>
                      {callbackDate ? (
                        <>
                          <dt>Callback requested</dt>
                          <dd>{callbackDate}</dd>
                        </>
                      ) : null}
                    </dl>
                  )}
                </div>
                <button type="button" className="card-link" data-customer-view="history">
                  View customer history
                  <Icon id="arrow" />
                </button>
              </section>
              <section className="card sf-guidance">
                <span className="eyebrow" id="sf-guidance-eyebrow">
                  CONVERSATION GUIDE
                </span>
                <h3 id="sf-guidance-title">
                  {useFixture ? `Start with how ${HORSE.name} is doing.` : `Start with how ${possessive} horse is doing.`}
                </h3>
                <p id="sf-guidance-copy">
                  Offer useful help. Confirm {possessive} current need before discussing the next
                  order.
                </p>
              </section>
              <section className="card">
                <div className="context">
                  <div className="eyebrow">CURRENT TASK</div>
                  <dl>
                    <dt>Purpose</dt>
                    <dd id="sf-task-purpose">
                      {useFixture ? 'Reactivation' : task ? task.subject || 'Follow-up task' : callbackLabel ?? 'None assigned'}
                    </dd>
                    <dt>Channel</dt>
                    <dd id="sf-task-channel">{useFixture ? 'Text' : task ? 'HubSpot task' : callbackLabel ? 'VanillaSoft callback' : 'None'}</dd>
                    <dt>Recipient</dt>
                    <dd id="sf-task-recipient">{customer.fullName}</dd>
                    <dt>Status</dt>
                    <dd id="sf-task-status">
                      {sent
                        ? 'Reply sent'
                        : retained
                          ? 'Retained'
                          : useFixture || task
                            ? 'Ready for reply'
                            : callbackLabel
                              ? 'Callback requested'
                              : 'No task'}
                    </dd>
                  </dl>
                </div>
              </section>
            </aside>
          </div>
        </div>
        {useFixture ? (
          <section className="next" aria-label="Next work preview">
            <span className="eyebrow">UP NEXT</span>
            <div className="avatar">{NEXT_UP.initials}</div>
            <div>
              <strong>{NEXT_UP.name}</strong>
              <p>{NEXT_UP.description}</p>
            </div>
            <div className="reason">
              <strong>{NEXT_UP.reason.split('·')[0]?.trim() ?? ''}</strong>
              <p>{NEXT_UP.reason.split('·')[1]?.trim() ?? ''}</p>
            </div>
            <Icon id="arrow" />
          </section>
        ) : <QueueUpNext customerId={customerId} />}
      </DataBoundary>
      <ActionFooter
        disabled={finishDisabled}
        noteWhenDisabled={helper}
        noteWhenEnabled={helper}
        summaryStrong={`Special follow-up · ${customer.fullName}`}
        summaryBody="AI selects the next eligible work after this task is saved."
        onFinish={() => navigate(returnTarget.path, { state: returnTarget.state })}
      />
      {scoped.previewAllowed && !stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Loading follow-up…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No follow-up assigned',
        body: 'When a reactivation or routine task is routed to you it appears here.',
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Follow-up could not load',
        detail: 'The task adapter did not respond.',
        errorCode: 'FOLLOWUP_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'sf-customer-context', reason: 'horse_timeline_slow' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Follow-up send is paused',
        reason: 'Live-write gate OFF for Follow-up Send.',
        gateName: 'LIVE_WRITE_GATE_FOLLOWUP_SEND',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Sending follow-up reply…',
        actionName: 'followup_send',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default SpecialFollowupsScreen;
